<script setup lang="ts">
import { joinURL } from 'ufo';

import { LeadIntent, type SaleAssetBusEntity, useApiUpdateAccountPersonalData } from '~/apiClient';
import type { PlatformSaleItemEntity } from '~/types/sale.type';

// CONSTANTS
const documents = [
    'registrationDocument',
    'registrationDocumentPartTwo',
    'technicalSheet',
    'cocDocument',
    'maintenanceCard',
    'inspectionAndMileageCheck',
    'inspectionSticker',
] as const;

type DocumentKey = (typeof documents)[number];

// PROPS
interface Props {
    asset: SaleAssetBusEntity;
    sale: PlatformSaleItemEntity;
}

const { sale } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { trackFileDownload } = useTracking();
const { data: session, isLoggedIn } = useAuthUtils();
const { captureLeadContacts } = useSdpLead();
const { openAuthModal } = useModals();
const { mutateAsync: mutatePersonalData } = useApiUpdateAccountPersonalData();
const { logError } = useLogs();

// DATA
const baseURL = useRuntimeConfig().public.cdnMediaBaseUrl;

// HANDLERS
// Documents are reserved for logged-in users: a guest goes through the lead and login flow instead (PT-893)
const onDownloadDocument = async (doc: DocumentKey): Promise<void> => {
    if (isLoggedIn.value) {
        trackFileDownload(doc, 'pdf');

        return;
    }

    const customerLeadContacts = await captureLeadContacts({ sale, intentType: LeadIntent.DocumentDownload });

    if (!customerLeadContacts) return;

    if (!(await openAuthModal({ email: customerLeadContacts.email, phone: customerLeadContacts.phone, action: 'SDP_DOCUMENT_DOWNLOAD' }))) return;

    if (!session.value?.user.phone) {
        mutatePersonalData({ data: { phone: customerLeadContacts.phone } }).catch(error =>
            logError('Error while saving phone after SDP documents login', { saleId: sale.id, userId: session.value?.user.id, apiError: error })
        );
    }
};
</script>

<template>
    <div class="flex flex-col gap-6">
        <h3 class="text-highlighted text-2xl font-semibold md:text-3xl">{{ tt('sdp.papers') }}</h3>
        <div class="flex flex-wrap gap-3">
            <BaseTooltip v-if="!asset.cocDocument" :text="tt('sdp.cocDocumentAbsent')" :content="{ side: 'top' }">
                <UButton color="neutral" variant="subtle" size="lg" :label="tt(`sdp.cocDocument`)" leading-icon="lucide:file-x-2" disabled />
            </BaseTooltip>
            <template v-for="doc in documents" :key="doc">
                <UButton
                    v-if="asset[doc]"
                    variant="soft"
                    size="lg"
                    :label="tt(`sdp.${doc}`)"
                    leading-icon="lucide:download"
                    :to="isLoggedIn ? joinURL(baseURL, asset[doc]!) : undefined"
                    :target="isLoggedIn ? '_blank' : undefined"
                    @click="onDownloadDocument(doc)"
                />
            </template>
        </div>
    </div>
</template>
